import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Bot, X, Send, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Show the assistant only inside an authenticated borrower / broker / admin session.
function useVisible() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/borrower")) return !!localStorage.getItem("th_borrower_token");
  if (pathname.startsWith("/broker")) return !!localStorage.getItem("th_broker_token");
  if (pathname.startsWith("/admin")) return !!localStorage.getItem("th_admin_token");
  return false;
}

const MatchCard = ({ m }) => (
  <div className="rounded-md border border-[#2A2A22] bg-[#0F0F0A] p-2.5 text-[11.5px]" data-testid="assistant-match-card">
    <div className="flex items-center gap-1.5 mb-1">
      <span className="font-semibold text-[#ECECE4]">{m.name}</span>
      {m.confirmed ? (
        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide text-[#3DBB8E] border border-[#25604a] rounded-full px-1.5 py-0.5"><ShieldCheck size={9} /> verified</span>
      ) : (
        <span className="text-[9px] uppercase tracking-wide text-[#8A8A8A] border border-[#2A2A22] rounded-full px-1.5 py-0.5">{m.source}</span>
      )}
    </div>
    {m.address ? <div className="text-[#B9B9AE]">{m.address}</div> : null}
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[#9A9A90]">
      {m.phone ? <span>☎ {m.phone}</span> : null}
      {m.fax ? <span>⎙ {m.fax}</span> : null}
      {m.email ? <span>✉ {m.email}</span> : null}
      {m.routing ? <span>ABA {m.routing}</span> : null}
      {m.swift ? <span>SWIFT {m.swift}</span> : null}
    </div>
  </div>
);

export default function CounterpartyAssistant() {
  const visible = useVisible();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi — I can look up a counterparty's address, phone, fax, email or routing (verified from our directory + FDIC), or review client intake and flag missing fields. Who are we looking for?" },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((p) => [...p, { role: "user", content: text }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/assistant/counterparty`, { message: text, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMsgs((p) => [...p, { role: "assistant", content: r.data.reply, matches: r.data.matches || [] }]);
    } catch (e) {
      setMsgs((p) => [...p, { role: "assistant", content: "⚠ " + (e?.response?.data?.detail || "Assistant unavailable right now.") }]);
    }
    setBusy(false);
  };

  if (!visible) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-launch-btn"
          className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 px-4 py-3 rounded-full bg-[#B8860B] text-[#0A0A0A] font-semibold text-[13px] shadow-lg hover:bg-[#c9971b] transition-colors"
        >
          <Bot size={18} /> Desk Assistant
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-[60] w-[370px] max-w-[92vw] h-[560px] max-h-[82vh] flex flex-col rounded-xl border border-[#2A2A22] bg-[#0A0A0A] shadow-2xl overflow-hidden" data-testid="assistant-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1C1C16] bg-[#0F0F0A]">
            <div className="flex items-center gap-2">
              <Bot size={16} className="text-[#B8860B]" />
              <div>
                <div className="text-[13px] font-semibold text-[#ECECE4] leading-none">Counterparty Desk Assistant</div>
                <div className="text-[10px] text-[#6d6d64] mt-0.5">Verified directory + FDIC · confirm before wiring</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close-btn" className="text-[#8A8A8A] hover:text-[#ECECE4]"><X size={16} /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"} data-testid={`assistant-msg-${m.role}`}>
                <div className={m.role === "user"
                  ? "max-w-[85%] rounded-lg bg-[#1a1606] border border-[#3a2f12] text-[#ECECE4] px-3 py-2 text-[12.5px] whitespace-pre-wrap"
                  : "max-w-[92%] rounded-lg bg-[#12120d] border border-[#2A2A22] text-[#D8D8CE] px-3 py-2 text-[12.5px] whitespace-pre-wrap leading-relaxed"}>
                  {m.content}
                  {m.matches && m.matches.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {m.matches.map((mm, j) => <MatchCard key={j} m={mm} />)}
                    </div>
                  )}
                  {/^UNVERIFIED/im.test(m.content) && (
                    <div className="mt-2 flex items-center gap-1 text-[10.5px] text-[#E4B94A]"><AlertTriangle size={11} /> Contains unverified info — confirm with the institution.</div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="assistant-typing">
                <div className="rounded-lg bg-[#12120d] border border-[#2A2A22] px-3 py-2 text-[#8A8A8A] text-[12px] flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Looking it up…</div>
              </div>
            )}
          </div>

          <div className="border-t border-[#1C1C16] p-2.5 flex items-end gap-2 bg-[#0F0F0A]">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="e.g. Find the payoff fax & routing for Truist Bank"
              rows={1}
              data-testid="assistant-input"
              className="flex-1 resize-none bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[12.5px] text-[#ECECE4] placeholder-[#5C5C5C] focus:outline-none focus:border-[#B8860B] max-h-24"
            />
            <button onClick={send} disabled={busy || !input.trim()} data-testid="assistant-send-btn"
              className="shrink-0 w-9 h-9 rounded-md bg-[#B8860B] text-[#0A0A0A] flex items-center justify-center hover:bg-[#c9971b] disabled:opacity-40">
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
